/** Installation uses the browser's controls; inventory is always loaded online. */
export function installPlatform(navigator = {}) {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh|Mac OS/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR/i.test(ua)) return 'mac-safari';
  if (/Firefox/i.test(ua)) return 'desktop-firefox';
  return 'desktop';
}

export function installInstructions(platform) {
  switch (platform) {
    case 'ios': return ['In Safari, tap Share, then Add to Home Screen and Add.', 'This adds an icon for browsing. Searching selected text through Share requires a separate Apple Shortcut setup.'];
    case 'android': return ['Open this site in Chrome and open the menu (⋮).', 'Choose Install app, then confirm. A simple shortcut may not appear in Share.', 'On another site, highlight a part number, tap Share, then choose Fix It Fast.'];
    case 'mac-safari': return ['Open Safari’s File menu.', 'Choose Add to Dock, then Add.'];
    case 'desktop-firefox': return ['Open this site in Chrome or Edge.', 'Open the browser menu and choose Install Fix It Fast or Apps → Install this site as an app.'];
    default: return ['Open your browser menu (⋮ or …).', 'Choose Install Fix It Fast or Apps → Install this site as an app.'];
  }
}

export function isInstalledApp(window = {}) {
  return window.navigator?.standalone === true || Boolean(window.matchMedia?.('(display-mode: standalone)').matches);
}

export function setupAppInstall(window, document) {
  const button = document?.getElementById('install-app');
  const dialog = document?.getElementById('install-app-dialog');
  const closeButton = document?.getElementById('install-app-close');
  const instructions = document?.getElementById('install-app-instructions');
  const title = document?.getElementById('install-app-title');
  if (!window || !button || !dialog || !closeButton || !instructions) return () => {};

  let deferredPrompt = null;
  let installed = isInstalledApp(window);
  let busy = false;
  let returnFocus = null;
  const listeners = [];
  const listen = (target, event, handler) => {
    target.addEventListener(event, handler);
    listeners.push(() => target.removeEventListener(event, handler));
  };
  const refreshButton = () => { button.hidden = installed; };
  const restoreFocus = () => {
    const target = returnFocus;
    returnFocus = null;
    if (target && !target.hidden && target.isConnected !== false) target.focus?.();
  };
  const closeInstructions = () => {
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    dialog.hidden = true;
    restoreFocus();
  };
  const showInstructions = () => {
    if (title) title.textContent = 'Add Fix It Fast';
    const steps = document.createElement('ol');
    for (const text of installInstructions(installPlatform(window.navigator))) {
      const item = document.createElement('li');
      item.textContent = text;
      steps.append(item);
    }
    instructions.replaceChildren(steps);
    returnFocus = document.activeElement || button;
    dialog.hidden = false;
    try {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } catch { dialog.setAttribute('open', ''); }
    closeButton.focus?.();
  };
  listen(window, 'beforeinstallprompt', event => {
    if (installed || typeof event.prompt !== 'function') return;
    event.preventDefault();
    deferredPrompt = event;
    refreshButton();
  });
  listen(window, 'appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    refreshButton();
    if (dialog.open) closeInstructions();
  });
  listen(button, 'click', async () => {
    if (installed || busy) return;
    if (!deferredPrompt) { showInstructions(); return; }
    const prompt = deferredPrompt;
    deferredPrompt = null; // A browser install event can only be prompted once.
    busy = true;
    button.disabled = true;
    try {
      // Keep this call in the click gesture, before any awaited operation.
      const result = await prompt.prompt();
      const choice = result?.outcome ? result : await prompt.userChoice;
      if (choice?.outcome === 'accepted') installed = true;
    } catch { if (!installed) showInstructions(); }
    finally { busy = false; button.disabled = false; refreshButton(); }
  });
  listen(closeButton, 'click', closeInstructions);
  listen(dialog, 'close', () => { dialog.hidden = true; restoreFocus(); });
  listen(dialog, 'cancel', event => { event.preventDefault(); closeInstructions(); });
  listen(dialog, 'keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); closeInstructions(); }
  });
  const displayMode = window.matchMedia?.('(display-mode: standalone)');
  if (displayMode?.addEventListener) listen(displayMode, 'change', event => {
    installed = event.matches || window.navigator?.standalone === true;
    refreshButton();
    if (installed && dialog.open) closeInstructions();
  });
  refreshButton();
  return () => listeners.forEach(remove => remove());
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupAppInstall(window, document), {once: true});
  else setupAppInstall(window, document);
}
